import React, { useMemo, useState } from 'react';
import { Alert, Button, Card, Radio, Space, Typography } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';

const { Paragraph, Text } = Typography;

function resolveObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function resolveArray(value) {
  return Array.isArray(value) ? value : [];
}

function resolveText(value) {
  return String(value ?? '').trim();
}

function resolveQuestions(payload = {}) {
  return resolveArray(resolveObject(payload).questions)
    .map((question) => resolveObject(question))
    .filter((question) => resolveText(question.question_id));
}

export function MessagePatchQuestionNodeCard({
  item = {},
  onAction,
  themeTokens,
}) {
  const payload = resolveObject(item.payload);
  const tokens = resolveObject(themeTokens);
  const questions = useMemo(() => resolveQuestions(payload), [payload]);
  const [selected, setSelected] = useState({});
  const submit = resolveObject(payload.submit);
  const requiredQuestions = questions.filter((question) => question.required !== false);
  const canSubmit = [questions.length > 0, requiredQuestions.every((question) => {
    return Boolean(resolveText(selected[resolveText(question.question_id)]));
  })].every(Boolean);

  const handleSubmit = () => {
    const answers = questions
      .map((question) => {
        const questionId = resolveText(question.question_id);
        const optionId = resolveText(selected[questionId]);
        return optionId ? { question_id: questionId, option_id: optionId } : null;
      })
      .filter(Boolean);
    if (typeof onAction !== 'function') return;
    onAction({
      interactionKey: item.interactionKey,
      action_key: [submit.action_key, 'submit_patch_question_answer'].find(Boolean),
      payload: {
        patch_question_node: payload,
        answers,
      },
    });
  };
  const handleCancel = () => {
    if (typeof onAction !== 'function') return;
    onAction({
      interactionKey: item.interactionKey,
      action_key: 'cancel_patch_question_answer',
      payload: {
        patch_question_node: payload,
      },
    });
  };

  if (item.presentation?.interactive === false) return null;
  return (
    <Card
      size="small"
      style={{
        background: tokens.surfaceBg,
        borderColor: tokens.surfaceBorder,
      }}
    >
      <Space orientation="vertical" size={10} style={{ width: '100%' }}>
        {item.presentation?.errorMessage ? <Alert type="error" showIcon title={item.presentation.errorMessage} /> : null}
        <Space align="center" size={8}>
          <QuestionCircleOutlined />
          <Text strong style={{ color: tokens.textPrimary }}>
            {[resolveText(payload.title), '确认后应用修改'].find(Boolean)}
          </Text>
        </Space>
        {resolveText(payload.description) ? (
          <Paragraph style={{ color: tokens.textSecondary, marginBottom: 0 }}>
            {resolveText(payload.description)}
          </Paragraph>
        ) : null}
        {questions.map((question) => {
          const questionId = resolveText(question.question_id);
          return (
            <Space key={questionId} orientation="vertical" size={6} style={{ width: '100%' }}>
              <Text strong style={{ color: tokens.textPrimary }}>
                {resolveText(question.label) ? `${resolveText(question.label)}. ` : ''}
                {resolveText(question.question_text)}
              </Text>
              <Radio.Group
                onChange={(event) => setSelected((current) => ({
                  ...current,
                  [questionId]: event.target.value,
                }))}
                value={selected[questionId]}
              >
                <Space orientation="vertical" size={4}>
                  {resolveArray(question.options).map((option) => {
                    const optionItem = resolveObject(option);
                    const optionId = resolveText(optionItem.option_id);
                    return (
                      <Radio key={optionId} value={optionId}>
                        <Text style={{ color: tokens.textPrimary }}>
                          {[resolveText(optionItem.label), optionId].find(Boolean)}：{resolveText(optionItem.text)}
                        </Text>
                      </Radio>
                    );
                  })}
                </Space>
              </Radio.Group>
            </Space>
          );
        })}
        <Space size={8}>
          <Button
            disabled={!canSubmit}
            onClick={handleSubmit}
            size="small"
            type={canSubmit ? 'primary' : 'default'}
          >
            {[resolveText(submit.label), '确认应用'].find(Boolean)}
          </Button>
          <Button onClick={handleCancel} size="small" type="text">
            取消本次修改
          </Button>
        </Space>
      </Space>
    </Card>
  );
}

export default MessagePatchQuestionNodeCard;
